import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Box, Button, Typography } from '@mui/material';
import clsx from 'clsx';
import { BUSINESS_TOOL_MODE } from 'constants/businessTool';
import { SUBSCRIPTION_PLAN as SUBSCRIPTION_PLAN_PATH } from 'constants/paths';
import { useBusinessToolSessionStore } from 'store/useBusinessToolStore';
import { getSetBusinessToolMode } from 'store/useBusinessToolStore/selectors';

import useCreateBusinessPlanHandler from 'pages/Workspace/hooks/useCreateBusinessPlanHandler';

import { ReactComponent as PlusIcon } from 'assets/icons/PlusIcon.svg';

import { useBusinessPlanTypeAvailability } from 'hooks/useBusinessPlanTypeAvailability';

import { IBusinessPlanButtonOption } from 'interfaces/businessPlan';
import { IUserSubscriptionEntity, SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { Loader } from 'components/_basic/Loader/Loader';
import { TooltipComponent } from 'components/_basic/TooltipComponent/TooltipComponent';
import { useHeaderLogic } from 'components/Header/HeaderLogicProvider';

import styles from './CreateBusinessPlanButton.module.scss';

interface IBusinessPlanButtonProps extends IBusinessPlanButtonOption {
  showAvailablePlansCount?: boolean;
  userSubscription?: IUserSubscriptionEntity | null;
}

export const BusinessPlanButton: FC<IBusinessPlanButtonProps> = ({
  template,
  label,
  icon,
  color,
  borderColor,
  backgroundColor,
  backgroundColorHover,
  showAvailablePlansCount = false,
  userSubscription,
}) => {
  const { t } = useTranslation();

  const { isAvailable, limit } = useBusinessPlanTypeAvailability(template);
  const setBusinessToolMode = useBusinessToolSessionStore(getSetBusinessToolMode);
  const { isStarterSubscription } = useHeaderLogic();

  const isUserPlanExhausted = !isAvailable && showAvailablePlansCount;
  const isNotStandardPlan = userSubscription && userSubscription.subscriptionPlan !== SUBSCRIPTION_PLAN.STANDARD;

  const { createBusinessPlanHandler, isPending } = useCreateBusinessPlanHandler({
    type: template,
    isPlanAvailable: isAvailable,
  });

  const clickHandler = () => {
    setBusinessToolMode(BUSINESS_TOOL_MODE.EDIT);
    createBusinessPlanHandler();
  };

  const cssVariables = {
    '--plan-color': color,
    '--plan-background': backgroundColor,
    '--plan-borderColor': borderColor,
    '--plan-background-hover': backgroundColorHover,
  } as React.CSSProperties;

  return (
    <Button
      style={cssVariables}
      className={clsx(
        styles.button,
        (isUserPlanExhausted || (isStarterSubscription && template !== 'ONE_PAGE_SHORT')) && styles.disabled,
        showAvailablePlansCount && styles.fullWidth,
      )}
      onClick={clickHandler}
    >
      <Box className={styles.buttonTextStyles}>
        <Box className={styles.icon}>{icon}</Box>
        <Typography variant="h5" className={styles.title}>
          {label}
        </Typography>
        {showAvailablePlansCount && (
          <Typography className={styles.planAvailabilityCount} variant="body2">
            {limit} {t('left')}
          </Typography>
        )}
      </Box>

      {!isUserPlanExhausted && (
        <Box className={styles.plus}>{isPending ? <Loader size={'small'} /> : <PlusIcon />}</Box>
      )}
      {isUserPlanExhausted && isNotStandardPlan && (
        <TooltipComponent title={t('maximumProjectsCreated')}>
          <Typography variant="h6">
            <Link to={SUBSCRIPTION_PLAN_PATH} className={styles.upgradeTextStyles}>
              {t('upgrade')}
            </Link>
          </Typography>
        </TooltipComponent>
      )}
    </Button>
  );
};
